import FloatingCard from '../../../components/general/FloatingCard';
import { SecondaryButton } from '../../../components/buttons/SecondaryButton';
import { FlightProgressIndicator } from './FlightProgressIndicator';
import { MissionLogsCard } from './MissionLogsCard';

import { useGcsStore } from '../../../store/useGcsStore';
import { useDialogQueueStore } from '../../../store/useDialogQueueStore';
import type { VehicleId } from '../../../types/gcs/vehicle';
import type { MenuItemTileProps } from '../../../components/general/MenuItemTile';
import VehiclePanelHeader from './VehiclePanelHeader';
import { McErrorTile } from './McErrorTile';
import SetMissionNoteDialog from '../../../components/dialogs/SetMissionNoteDialog';
import VehicleModuleVersionsDialog from '../../../components/dialogs/VehicleModuleVersionsDialog';
import ConfirmSafeUpDialog from '../../../components/dialogs/ConfirmSafeUpDialog';
import ConfirmRtlDialog from '../../../components/dialogs/ConfirmRtlDialog';
import ConfirmEmergencyLandDialog from '../../../components/dialogs/ConfirmEmergencyLandDialog';
import ConfirmFtsDialog from '../../../components/dialogs/ConfirmFtsDialog';
import { en } from '../../../locales/en';
import { MissionStepper } from './MissionStepper';

interface InflightVehiclePanelProps {
  vehicleId: VehicleId;
  gcsId: string;
  onClose: () => void;
  onFocusVehicle: () => void;
}

function InflightVehiclePanel({ vehicleId, gcsId, onClose, onFocusVehicle }: InflightVehiclePanelProps) {
  const { showDialog, hideDialog } = useDialogQueueStore();

  const liveMission = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    if (!gcsData) return null;
    return gcsData.liveMissionsMap.get(vehicleId);
  });

  const menuItems: MenuItemTileProps[] = [
    {
      label: en.vehiclePanelMenuItems.showVersions,
      onClick: () => {
        showDialog({
          id: `vehicle-module-versions-${vehicleId}`,
          component: <VehicleModuleVersionsDialog vehicleId={vehicleId} onClose={hideDialog} />
        });
      }
    },
    {
      label: en.vehiclePanelMenuItems.missionNote,
      onClick: () => {
        if (!liveMission) return;
        showDialog({
          id: 'set-mission-note',
          component: (
            <SetMissionNoteDialog
              gcsId={gcsId}
              vehicleId={vehicleId}
              missionId={liveMission.missionId}
              onClose={() => useDialogQueueStore.getState().hideDialog()}
            />
          )
        });
      }
    }
  ];

  return (
    <FloatingCard>
      <VehiclePanelHeader
        vehicleId={vehicleId}
        gcsId={gcsId}
        onFocusVehicle={onFocusVehicle}
        onClose={onClose}
        menuItems={menuItems}
      />
      <McErrorTile gcsId={gcsId} vehicleId={vehicleId} />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}
      >
        {/* Progress Indicator */}
        {liveMission && <FlightProgressIndicator gcsId={gcsId} missionId={liveMission.missionId} />}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <SecondaryButton
            text={en.inflightPanel.safeUpButton}
            callback={() => {
              if (!liveMission) return;
              showDialog({
                id: 'confirm-safe-up-land',
                component: (
                  <ConfirmSafeUpDialog
                    gcsId={gcsId}
                    vehicleId={vehicleId}
                    missionId={liveMission.missionId}
                    onClose={() => useDialogQueueStore.getState().hideDialog()}
                  />
                )
              });
            }}
          />
          <SecondaryButton
            text={en.inflightPanel.rtlButton}
            callback={() => {
              if (!liveMission) return;
              showDialog({
                id: 'confirm-rtl',
                component: (
                  <ConfirmRtlDialog
                    gcsId={gcsId}
                    vehicleId={vehicleId}
                    missionId={liveMission.missionId}
                    onClose={() => useDialogQueueStore.getState().hideDialog()}
                  />
                )
              });
            }}
          />
          <SecondaryButton
            text={en.inflightPanel.landButton}
            callback={() => {
              if (!liveMission) return;
              showDialog({
                id: 'confirm-emergency-land',
                component: (
                  <ConfirmEmergencyLandDialog
                    gcsId={gcsId}
                    vehicleId={vehicleId}
                    missionId={liveMission.missionId}
                    onClose={() => useDialogQueueStore.getState().hideDialog()}
                  />
                )
              });
            }}
          />
        </div>

        <MissionStepper gcsId={gcsId} vehicleId={vehicleId} />

        {/* Mission Logs */}
        {liveMission && (
          <MissionLogsCard gcsId={gcsId} missionId={liveMission.missionId} title={en.inflightPanel.missionLogs} />
        )}

        {/* FTS Button */}
        <SecondaryButton
          text={en.inflightPanel.ftsButton}
          callback={() => {
            if (!liveMission) return;
            showDialog({
              id: 'confirm-fts',
              component: (
                <ConfirmFtsDialog
                  gcsId={gcsId}
                  vehicleId={vehicleId}
                  missionId={liveMission.missionId}
                  onClose={() => useDialogQueueStore.getState().hideDialog()}
                />
              )
            });
          }}
        />
      </div>
    </FloatingCard>
  );
}

export default InflightVehiclePanel;
